import React, { useEffect, useState, ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  UserEntity,
  PhotoEntity,
  useAdminUpdateUserMutation,
  useAdminDeleteUserMutation,
  useLazyFetchUserProfileQuery,
  ONBOARDING_STEP_LABEL,
  OnboardingStep,
  INTERVIEW_TOPIC,
  UserType,
  UserTypeLabel,
} from 'src/store/userApi';
import {
  useAdminCreatePhotoMutation,
  useAdminDeletePhotoMutation,
} from 'src/store/photoApi';
import { useGenSummaryMutation } from 'src/store/aiApi';
import { useMatchOneUserMutation } from 'src/store/matchTestApi';
import { selectOptions } from 'src/store/optionSlice';
import { useSelector } from 'react-redux';
import { Alert } from 'src/ui/Alert';
import { Button } from 'src/ui/Button';
import { Card } from 'src/ui/Card';
import { Checkbox } from 'src/ui/Checkbox';
import { Dropdown, MenuItem } from 'src/ui/Dropdown';
import { Icon } from 'src/ui/Icon';
import { Input } from 'src/ui/Input';
import { Modal } from 'src/ui/Modal';
import { Select } from 'src/ui/Select';
import { Spinner } from 'src/ui/Spinner';
import { Textarea } from 'src/ui/Textarea';
import { useToast } from 'src/ui/Toast';
import ProfileField, { ProfileFieldProps } from '../match-test/profileField';
import ProfileModal from '../match-test/profileModal';
import { buildOptionsMap, groupProfileKeys } from './profileFieldUtils';
import UserProfileReadOnly from './userProfileReadOnly';
import HiddenProfileFields from './hiddenProfileFields';
import _cloneDeep from 'lodash/cloneDeep';
import _unset from 'lodash/unset';
import _get from 'lodash/get';
import _set from 'lodash/set';
import _ from 'lodash';

// Virtual/computed properties that are getters in the backend entity and not actual database columns
// These should be readonly and excluded from save payloads
const VIRTUAL_PROFILE_FIELDS = new Set([
  'age',
  'zodiac',
  'chineseZodiac',
  'education',
  'ethnicity',
  'ethnicitySeek',
  'homeTown',
  'residentStatus',
  'religion',
  'smoking',
  'drinking',
  'drug',
  'spending',
  'occupation',
  'languages',
]);

interface Option {
  labels: any;
}

const INTERVIEW_TOPIC_LABELS = {
  [INTERVIEW_TOPIC.INIT]: 'Initial',
  [INTERVIEW_TOPIC.LIFE_STYLE]: 'Life Style',
  [INTERVIEW_TOPIC.FAMILY]: 'Family',
  [INTERVIEW_TOPIC.RELATIONSHIP]: 'Relationship',
};

interface UserProfileEditorProps {
  selectedUser: UserEntity | null;
  onUserDeleted?: () => void;
}

const UserProfileEditor = ({
  selectedUser,
  onUserDeleted,
}: UserProfileEditorProps) => {
  const [user, setUser] = useState<UserEntity | null>(null);
  const [isEditing, setIsEditing] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [optionsMap, setOptionsMap] = useState<Record<string, Option>>({});
  // The AI-generated summary draft shown beneath the current summary; `null`
  // when no draft is pending.
  const [genDraft, setGenDraft] = useState<string | null>(null);
  const [isSavingSummary, setIsSavingSummary] = useState(false);
  const [isProfileModalOpen, setIsProfileModalOpen] = useState(false);
  const [isDeleteOpen, setIsDeleteOpen] = useState(false);
  const [isPhotoModalOpen, setIsPhotoModalOpen] = useState(false);
  const [isDeletePhotoOpen, setIsDeletePhotoOpen] = useState(false);
  const [activePhotoIndex, setActivePhotoIndex] = useState(0);
  const [photoPendingDelete, setPhotoPendingDelete] =
    useState<PhotoEntity | null>(null);
  const navigate = useNavigate();

  const allOptions = useSelector(selectOptions);
  const [adminUpdateUser] = useAdminUpdateUserMutation();
  const [adminDeleteUser] = useAdminDeleteUserMutation();
  const [fetchUserProfile] = useLazyFetchUserProfileQuery();
  const [adminCreatePhoto, { isLoading: isUploadingPhoto }] =
    useAdminCreatePhotoMutation();
  const [adminDeletePhoto, { isLoading: isDeletingPhoto }] =
    useAdminDeletePhotoMutation();
  const [genSummary, genSummaryStatus] = useGenSummaryMutation();
  const [matchOneUser, matchOneUserStatus] = useMatchOneUserMutation();
  const push = useToast();

  useEffect(() => {
    if (Object.keys(allOptions).length) {
      setOptionsMap(buildOptionsMap(allOptions));
    }
  }, [allOptions]);

  const cloneSelectedUser = (): UserEntity | null => {
    if (!selectedUser) return null;
    return cloneAndStripVirtuals(selectedUser);
  };

  const cloneAndStripVirtuals = (src: UserEntity): UserEntity => {
    const userData = _.cloneDeep(src);
    if (userData.profile) {
      VIRTUAL_PROFILE_FIELDS.forEach((field) => {
        delete userData.profile[field];
      });
    }
    return userData;
  };

  const refreshUserFromServer = async (userId: string) => {
    try {
      const fresh = await fetchUserProfile(userId).unwrap();
      setUser(cloneAndStripVirtuals(fresh));
    } catch (err) {
      console.error('Failed to refresh user profile after photo change:', err);
    }
  };

  const mergePhotosById = (
    server: PhotoEntity[],
    created: PhotoEntity[]
  ): PhotoEntity[] => {
    const byId = new Map(server.map((p) => [p.id, p]));
    for (const c of created) {
      if (!byId.has(c.id)) byId.set(c.id, c);
    }
    return [...byId.values()].sort((a, b) => a.type - b.type);
  };

  const reconcileAfterUpload = async (
    userId: string,
    createdPhotos: PhotoEntity[]
  ) => {
    try {
      const fresh = await fetchUserProfile(userId).unwrap();
      setUser({
        ...cloneAndStripVirtuals(fresh),
        photos: mergePhotosById(fresh.photos ?? [], createdPhotos),
      });
    } catch (err) {
      console.error('Failed to reconcile user profile after upload:', err);
    }
  };

  const onUploadPhoto = () => {
    if (!user) return;
    const usedTypes = new Set((user.photos ?? []).map((p) => p.type));
    const freeSlots = [0, 1, 2, 3, 4, 5].filter((i) => !usedTypes.has(i));
    if (freeSlots.length === 0) return;
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = 'image/*';
    input.multiple = true;
    input.click();
    input.onchange = async () => {
      const files = Array.from(input.files ?? []);
      if (files.length === 0) return;

      const toUpload = files.slice(0, freeSlots.length);
      const skipped = files.length - toUpload.length;
      if (skipped > 0) {
        push({
          title: 'Some photos were not uploaded',
          message: `Only ${freeSlots.length} slot${
            freeSlots.length === 1 ? ' was' : 's were'
          } free; ${skipped} photo${skipped === 1 ? '' : 's'} skipped.`,
          type: 'warning',
        });
      }

      let successCount = 0;
      let firstError: string | null = null;
      const createdPhotos: PhotoEntity[] = [];
      for (let i = 0; i < toUpload.length; i++) {
        const file = toUpload[i];
        const slot = freeSlots[i];
        const formData = new FormData();
        formData.append('photo', file);
        formData.append('type', String(slot));
        formData.append('userId', user.id);
        try {
          const created = await adminCreatePhoto({
            userId: user.id,
            formData,
          }).unwrap();
          if (!created?.id) {
            throw new Error((created as any)?.error ?? 'Upload failed');
          }
          setUser((prev) =>
            prev ? { ...prev, photos: [...(prev.photos ?? []), created] } : prev
          );
          createdPhotos.push(created);
          successCount++;
        } catch (err: any) {
          if (!firstError) {
            firstError = err?.data?.message ?? err?.message ?? 'Upload failed';
          }
        }
      }

      if (successCount > 0) {
        push({
          title: `${successCount} photo${
            successCount === 1 ? '' : 's'
          } uploaded`,
          type: 'success',
        });
        await reconcileAfterUpload(user.id, createdPhotos);
      }
      if (firstError) {
        push({
          title: 'Some uploads failed',
          message: firstError,
          type: 'error',
        });
      }
    };
  };

  const onRequestDeletePhoto = (photo: PhotoEntity) => {
    setPhotoPendingDelete(photo);
    setIsDeletePhotoOpen(true);
  };

  const onConfirmDeletePhoto = async () => {
    if (!user || !photoPendingDelete) return;
    try {
      await adminDeletePhoto({
        userId: user.id,
        photoId: photoPendingDelete.id,
      }).unwrap();
      push({ title: 'Photo deleted', type: 'success' });
      setIsPhotoModalOpen(false);
      await refreshUserFromServer(user.id);
      setActivePhotoIndex(0);
    } catch (err: any) {
      push({
        title: 'Delete failed',
        message: err?.data?.message ?? err.message,
        type: 'error',
      });
    } finally {
      setIsDeletePhotoOpen(false);
      setPhotoPendingDelete(null);
    }
  };

  useEffect(() => {
    setUser(cloneSelectedUser());
    // Drop any pending generated draft when switching users.
    setGenDraft(null);
    setIsEditing(false);
    setSearchTerm('');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedUser]);

  const onEdit = () => setIsEditing(true);

  const onCancel = () => {
    setUser(cloneSelectedUser());
    setSearchTerm('');
    setIsEditing(false);
  };

  const onFieldValueChange = (key: string, value: any, isUser = false) => {
    if (!user) return;

    const newUserObject = _cloneDeep(user);
    if (isUser) {
      _set(newUserObject, key, value);
    } else {
      _set(newUserObject.profile, key, value);
    }

    setUser(newUserObject);
  };

  const onInterviewProgressChange = (
    field: 'currentTopic' | 'completedTopics',
    value: any
  ) => {
    if (!user) return;

    const newUserObject = _cloneDeep(user);
    if (!newUserObject.interviewProgress) {
      newUserObject.interviewProgress = {
        currentTopic: null,
        completedTopics: [],
      };
    }

    if (field === 'completedTopics') {
      const currentCompleted =
        newUserObject.interviewProgress.completedTopics || [];
      if (currentCompleted.includes(value as INTERVIEW_TOPIC)) {
        newUserObject.interviewProgress.completedTopics =
          currentCompleted.filter((topic: INTERVIEW_TOPIC) => topic !== value);
      } else {
        newUserObject.interviewProgress.completedTopics = [
          ...currentCompleted,
          value,
        ];
      }
    } else {
      newUserObject.interviewProgress[field] = value === '' ? null : value;
    }

    setUser(newUserObject);
  };

  const onFieldDelete = (key: string) => {
    if (!user) return;

    const newUserObject = _cloneDeep(user);
    if (_get(user.profile, key)) {
      _unset(newUserObject.profile, key);
    }

    setUser(newUserObject);
  };

  const onSave = async () => {
    if (!user) return;

    try {
      const userToSave = _.cloneDeep(user);
      if (userToSave.profile) {
        VIRTUAL_PROFILE_FIELDS.forEach((field) => {
          delete userToSave.profile[field];
        });
        delete userToSave.profile.hiddenProfileFields;
      }

      const response = await adminUpdateUser(userToSave).unwrap();
      if (response) {
        push({
          title: 'Profile saved successfully',
          type: 'success',
        });
        setIsEditing(false);
      }
    } catch (error) {
      push({
        title: 'Failed to save profile',
        type: 'error',
      });
    }
  };

  const onDelete = async () => {
    if (!user) return;

    try {
      await adminDeleteUser(user.id).unwrap();
      push({
        title: 'User deleted successfully',
        type: 'success',
      });
      setIsDeleteOpen(false);
      onUserDeleted?.();
    } catch (error) {
      push({
        title: 'Failed to delete user',
        type: 'error',
      });
      setIsDeleteOpen(false);
    }
  };

  const onDownload = async () => {
    if (!user?.profile) {
      return;
    }
    setIsProfileModalOpen(true);
  };

  const onProfileModalClose = () => {
    setIsProfileModalOpen(false);
  };

  const onGenSummary = async () => {
    if (!user) return;

    try {
      const genSummaryResponse = await genSummary(user.id).unwrap();
      setGenDraft(genSummaryResponse.summary);
      push({
        title: 'Summary generated successfully',
        type: 'success',
      });
    } catch (error) {
      push({
        title: 'Failed to generate summary',
        type: 'error',
      });
    }
  };

  const onSaveSummary = async () => {
    if (!user || !genDraft?.trim()) return;

    setIsSavingSummary(true);
    try {
      await adminUpdateUser({
        id: user.id,
        profile: { summary: genDraft },
      }).unwrap();
      // Reflect the save locally so the Bio section updates, then reconcile
      // with the server (skip the refetch mid-edit so it can't clobber other
      // unsaved field changes — the optimistic update already shows the value).
      setUser((prev) =>
        prev
          ? { ...prev, profile: { ...prev.profile, summary: genDraft } }
          : prev
      );
      if (!isEditing) {
        await refreshUserFromServer(user.id);
      }
      // Collapse the draft section and return the Bio to its default state.
      setGenDraft(null);
      push({ title: 'Summary saved', type: 'success' });
    } catch (error) {
      push({ title: 'Failed to save summary', type: 'error' });
    } finally {
      setIsSavingSummary(false);
    }
  };

  const onViewConversationHistory = () => {
    if (!user) return;
    navigate(`/conversation-history?userId=${user.id}`);
  };

  const onTriggerMatch = async () => {
    if (!user) return;

    try {
      const response = await matchOneUser({ userId: user.id }).unwrap();
      push({
        title: 'Match calculation started',
        message: response.message,
        type: 'success',
      });
    } catch (error) {
      push({
        title: 'Failed to trigger match calculation',
        message: 'Please try again later',
        type: 'error',
      });
    }
  };

  const renderPhotos = () => {
    if (!user?.photos || user.photos.length === 0) {
      return (
        <Card pad>
          <span
            style={{
              color: 'var(--text-muted)',
              textAlign: 'center',
              display: 'block',
              fontSize: 'var(--fs-sm)',
            }}
          >
            No photos uploaded
          </span>
        </Card>
      );
    }

    const photos = [...user.photos].sort((a, b) => a.type - b.type);
    const activePhoto = photos[activePhotoIndex];
    const atFirst = activePhotoIndex === 0;
    const atLast = activePhotoIndex === photos.length - 1;

    return (
      <>
        <div style={{ overflowX: 'auto', paddingBottom: 8 }}>
          <div className="row" style={{ gap: 12, alignItems: 'stretch' }}>
            {photos.map((photo, index) => (
              <div
                key={photo.id}
                style={{
                  width: 120,
                  height: 120,
                  flexShrink: 0,
                  cursor: 'pointer',
                  borderRadius: 'var(--radius-md)',
                  overflow: 'hidden',
                  border: '1px solid var(--border)',
                  position: 'relative',
                }}
                onClick={() => {
                  setActivePhotoIndex(index);
                  setIsPhotoModalOpen(true);
                }}
              >
                <img
                  src={
                    photo.sImage?.url || photo.mImage?.url || photo.lImage?.url
                  }
                  alt={`User ${index + 1}`}
                  style={{
                    width: '100%',
                    height: '100%',
                    objectFit: 'cover',
                  }}
                />
                <button
                  type="button"
                  className="btn btn-ghost btn-icon btn-sm"
                  aria-label={`Delete photo ${index + 1}`}
                  style={{
                    position: 'absolute',
                    top: 4,
                    right: 4,
                    background: 'rgba(0,0,0,0.5)',
                    color: 'white',
                    borderRadius: 'var(--radius-sm)',
                  }}
                  onClick={(e) => {
                    e.stopPropagation();
                    onRequestDeletePhoto(photo);
                  }}
                >
                  <Icon name="trash" size={12} />
                </button>
              </div>
            ))}
          </div>
        </div>

        <Modal
          open={isPhotoModalOpen}
          onClose={() => setIsPhotoModalOpen(false)}
          width={900}
        >
          <div style={{ position: 'relative' }}>
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                minHeight: '60vh',
              }}
            >
              <img
                src={
                  activePhoto?.lImage?.url ||
                  activePhoto?.mImage?.url ||
                  activePhoto?.sImage?.url
                }
                alt={`User ${activePhotoIndex + 1}`}
                style={{
                  maxHeight: '80vh',
                  maxWidth: '100%',
                  objectFit: 'contain',
                }}
              />
            </div>
            {!atFirst && (
              <button
                type="button"
                className="btn btn-ghost btn-icon"
                aria-label="Previous photo"
                onClick={() => setActivePhotoIndex(activePhotoIndex - 1)}
                style={{
                  position: 'absolute',
                  top: '50%',
                  left: 8,
                  transform: 'translateY(-50%)',
                  borderRadius: '50%',
                  background: 'rgba(0,0,0,0.5)',
                  color: 'white',
                }}
              >
                <Icon name="chevron-left" size={24} />
              </button>
            )}
            {!atLast && (
              <button
                type="button"
                className="btn btn-ghost btn-icon"
                aria-label="Next photo"
                onClick={() => setActivePhotoIndex(activePhotoIndex + 1)}
                style={{
                  position: 'absolute',
                  top: '50%',
                  right: 8,
                  transform: 'translateY(-50%)',
                  borderRadius: '50%',
                  background: 'rgba(0,0,0,0.5)',
                  color: 'white',
                }}
              >
                <Icon name="chevron-right" size={24} />
              </button>
            )}
          </div>
          <div
            className="row"
            style={{
              marginTop: 12,
              alignItems: 'center',
              justifyContent: 'center',
              gap: 12,
            }}
          >
            <span
              style={{
                fontSize: 'var(--fs-sm)',
                color: 'var(--text-secondary)',
              }}
            >
              Photo {activePhotoIndex + 1} of {photos.length} · Type:{' '}
              {activePhoto?.type}
            </span>
            {activePhoto && (
              <Button
                size="sm"
                variant="secondary"
                leftIcon="trash"
                onClick={() => onRequestDeletePhoto(activePhoto)}
              >
                Delete
              </Button>
            )}
          </div>
        </Modal>
      </>
    );
  };

  const matchesFieldSearch = (key: string) =>
    !searchTerm || key.toLowerCase().includes(searchTerm.toLowerCase());

  const renderFieldEditorBox = (
    fieldKey: string,
    fieldValue: any,
    extraProps: Partial<ProfileFieldProps> = {}
  ) => (
    <div key={fieldKey} style={{ width: '100%' }}>
      <ProfileField
        fieldKey={fieldKey}
        fieldValue={fieldValue}
        enableDelete={false}
        onChange={(key, value) => {
          onFieldValueChange(key, value);
        }}
        {...extraProps}
      />
    </div>
  );

  const renderSectionCard = (title: string, children: ReactNode) => (
    <Card key={title} pad>
      <div
        style={{
          fontWeight: 600,
          fontSize: 'var(--fs-sm)',
          color: 'var(--text)',
          marginBottom: 12,
        }}
      >
        {title}
      </div>
      {children}
    </Card>
  );

  const renderProfileFields = () => {
    if (!user?.profile) {
      return <Alert color="blue">No profile data available</Alert>;
    }

    const profile = user.profile;

    const groupCards = groupProfileKeys(profile)
      .map((group) => {
        const keys = group.keys
          .filter((key) => key !== 'summary')
          .filter(matchesFieldSearch);
        if (keys.length === 0) return null;
        return renderSectionCard(
          group.title,
          <div className="col" style={{ gap: 16 }}>
            {keys.map((key) =>
              renderFieldEditorBox(key, profile[key] || '', {
                fieldOptions: optionsMap[key]?.labels,
              })
            )}
          </div>
        );
      })
      .filter(Boolean);

    const answerKeys = (
      profile.answers ? Object.keys(profile.answers).sort() : []
    ).filter(matchesFieldSearch);
    const answerCard =
      answerKeys.length > 0
        ? renderSectionCard(
            'Interview Answers',
            <div className="col" style={{ gap: 16 }}>
              {answerKeys.map((key) =>
                renderFieldEditorBox(
                  `answers.${key}`,
                  profile.answers?.[key] || '',
                  {
                    enableDelete: true,
                    onDelete: (fieldKey: string) => {
                      onFieldDelete(fieldKey);
                    },
                  }
                )
              )}
            </div>
          )
        : null;

    if (groupCards.length === 0 && !answerCard) {
      return (
        <Alert color="blue">
          {searchTerm
            ? `No fields found matching "${searchTerm}"`
            : 'No profile fields available'}
        </Alert>
      );
    }

    return (
      <div className="col" style={{ gap: 16 }}>
        {groupCards}
        {answerCard}
      </div>
    );
  };

  const renderUserInfoEdit = () => {
    if (!user) return null;
    return (
      <div className="col" style={{ gap: 16 }}>
        {/* User ID */}
        <div>
          <label
            style={{
              display: 'block',
              fontSize: 'var(--fs-sm)',
              fontWeight: 500,
              color: 'var(--text)',
              marginBottom: 4,
            }}
          >
            User ID
          </label>
          <Input value={user.id} readOnly />
        </div>

        {/* Email */}
        <div>
          <label
            style={{
              display: 'block',
              fontSize: 'var(--fs-sm)',
              fontWeight: 500,
              color: 'var(--text)',
              marginBottom: 4,
            }}
          >
            Email
          </label>
          <Input
            value={user.email}
            onChange={(e) => onFieldValueChange('email', e.target.value, true)}
          />
        </div>

        {/* Onboarding Step */}
        <div>
          <label
            style={{
              display: 'block',
              fontSize: 'var(--fs-sm)',
              fontWeight: 500,
              color: 'var(--text)',
              marginBottom: 4,
            }}
          >
            Onboarding Step
          </label>
          <Dropdown
            trigger={
              <Button variant="secondary" rightIcon="chevron-down">
                {ONBOARDING_STEP_LABEL[user.onboardingStep]}
              </Button>
            }
            width={260}
          >
            {Object.keys(ONBOARDING_STEP_LABEL).map((step) => (
              <MenuItem
                key={step}
                onClick={() =>
                  onFieldValueChange('onboardingStep', Number(step), true)
                }
              >
                {ONBOARDING_STEP_LABEL[step as unknown as OnboardingStep]}
              </MenuItem>
            ))}
          </Dropdown>
        </div>

        {/* User Type */}
        <div>
          <label
            style={{
              display: 'block',
              fontSize: 'var(--fs-sm)',
              fontWeight: 500,
              color: 'var(--text)',
              marginBottom: 4,
            }}
          >
            User Type
          </label>
          <Dropdown
            trigger={
              <Button variant="secondary" rightIcon="chevron-down">
                {UserTypeLabel[user.userType ?? UserType.NORMAL]}
              </Button>
            }
            width={180}
          >
            {Object.keys(UserTypeLabel).map((type) => (
              <MenuItem
                key={type}
                onClick={() =>
                  onFieldValueChange('userType', Number(type), true)
                }
              >
                {UserTypeLabel[type as unknown as UserType]}
              </MenuItem>
            ))}
          </Dropdown>
        </div>

        {/* Interview Stage */}
        <div>
          <label
            style={{
              display: 'block',
              fontSize: 'var(--fs-sm)',
              fontWeight: 500,
              color: 'var(--text)',
              marginBottom: 4,
            }}
          >
            Interview Stage
          </label>
          <Input
            value={user.interviewStage}
            type="number"
            onChange={(e) =>
              onFieldValueChange('interviewStage', Number(e.target.value), true)
            }
            placeholder="0: not started, -1 completed"
          />
        </div>

        {/* Interview Progress */}
        <div>
          <label
            style={{
              display: 'block',
              fontSize: 'var(--fs-sm)',
              fontWeight: 500,
              color: 'var(--text)',
              marginBottom: 4,
            }}
          >
            Interview Progress
          </label>
          <div className="col" style={{ gap: 12 }}>
            <div>
              <label
                style={{
                  display: 'block',
                  fontSize: 'var(--fs-sm)',
                  fontWeight: 500,
                  color: 'var(--text-secondary)',
                  marginBottom: 4,
                }}
              >
                Current Topic:
              </label>
              <Select
                value={user.interviewProgress?.currentTopic || ''}
                onChange={(e) =>
                  onInterviewProgressChange('currentTopic', e.target.value)
                }
                size="sm"
              >
                <option value="">Select current topic</option>
                {Object.entries(INTERVIEW_TOPIC_LABELS).map(
                  ([value, label]) => (
                    <option key={value} value={value}>
                      {label}
                    </option>
                  )
                )}
              </Select>
            </div>
            <div>
              <div
                style={{
                  fontSize: 'var(--fs-sm)',
                  fontWeight: 500,
                  color: 'var(--text-secondary)',
                  marginBottom: 8,
                }}
              >
                Completed Topics:
              </div>
              <div className="col" style={{ gap: 6 }}>
                {Object.entries(INTERVIEW_TOPIC_LABELS).map(
                  ([value, label]) => (
                    <label
                      key={value}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 8,
                        cursor: 'pointer',
                        fontSize: 'var(--fs-sm)',
                        color: 'var(--text)',
                      }}
                    >
                      <Checkbox
                        checked={
                          user.interviewProgress?.completedTopics?.includes(
                            value as INTERVIEW_TOPIC
                          ) || false
                        }
                        onChange={() =>
                          onInterviewProgressChange('completedTopics', value)
                        }
                      />
                      {label}
                    </label>
                  )
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  };

  if (!selectedUser) {
    return (
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          height: '100%',
        }}
      >
        <span style={{ color: 'var(--text-muted)', fontSize: 'var(--fs-lg)' }}>
          Select a user to view and edit their profile
        </span>
      </div>
    );
  }

  if (!user) {
    return (
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          height: '100%',
        }}
      >
        <Spinner size="lg" />
      </div>
    );
  }

  return (
    <div style={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      {/* Fixed Header */}
      <div
        style={{
          padding: 24,
          borderBottom: '1px solid var(--border)',
          flexShrink: 0,
        }}
      >
        <div
          className="row"
          style={{
            justifyContent: 'space-between',
            alignItems: 'center',
            gap: 12,
          }}
        >
          <div
            className="row"
            style={{ alignItems: 'center', gap: 8, minWidth: 0 }}
          >
            <span
              style={{
                fontWeight: 700,
                fontSize: 'var(--fs-lg)',
                color: 'var(--text)',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }}
            >
              {user.profile?.name || user.email}
            </span>
            {user.userType === UserType.SAMPLE && (
              <span
                className="badge badge-amber badge-sm"
                style={{ flexShrink: 0 }}
              >
                Sample
              </span>
            )}
            {user.userType === UserType.TEST && (
              <span
                className="badge badge-gray badge-sm"
                style={{ flexShrink: 0 }}
              >
                Test
              </span>
            )}
          </div>
          <div className="row" style={{ alignItems: 'center', gap: 12 }}>
            {isEditing ? (
              <>
                <Button variant="primary" onClick={onSave} size="sm">
                  Save Profile
                </Button>
                <Button variant="secondary" onClick={onCancel} size="sm">
                  Cancel
                </Button>
              </>
            ) : (
              <Button variant="primary" onClick={onEdit} size="sm">
                Edit Profile
              </Button>
            )}
            <Dropdown
              trigger={
                <Button variant="secondary" size="sm" rightIcon="chevron-down">
                  More Actions
                </Button>
              }
              align="right"
              width={220}
            >
              <MenuItem
                onClick={onTriggerMatch}
                disabled={matchOneUserStatus.isLoading}
              >
                Trigger Match
                {matchOneUserStatus.isLoading && (
                  <Spinner size="sm" style={{ marginLeft: 8 }} />
                )}
              </MenuItem>
              <MenuItem onClick={() => navigate(`/user-matches/${user.id}`)}>
                View Matches
              </MenuItem>
              <MenuItem onClick={onDownload}>Download Profile</MenuItem>
              <MenuItem onClick={onViewConversationHistory}>
                View Conversation History
              </MenuItem>
              <MenuItem onClick={() => setIsDeleteOpen(true)} danger>
                Delete User
              </MenuItem>
            </Dropdown>
          </div>
        </div>
      </div>

      {/* Scrollable Content */}
      <div style={{ flex: 1, overflowY: 'auto', padding: 24 }}>
        <div className="col" style={{ gap: 24 }}>
          {/* Photos Section */}
          <div>
            <div
              className="row"
              style={{
                alignItems: 'center',
                justifyContent: 'space-between',
                marginBottom: 16,
              }}
            >
              <span
                style={{
                  fontWeight: 700,
                  fontSize: 'var(--fs-md)',
                  color: 'var(--text)',
                }}
              >
                Photos
              </span>
              {(() => {
                const usedTypes = new Set(
                  (user?.photos ?? []).map((p) => p.type)
                );
                const nextSlot = [0, 1, 2, 3, 4, 5].find(
                  (i) => !usedTypes.has(i)
                );
                const allSlotsFull = nextSlot === undefined;
                const title = allSlotsFull
                  ? 'All 6 photo slots are filled. Delete one first.'
                  : undefined;
                return (
                  <Button
                    size="sm"
                    variant="primary"
                    leftIcon="plus"
                    disabled={allSlotsFull || isUploadingPhoto}
                    onClick={onUploadPhoto}
                    title={title}
                  >
                    {isUploadingPhoto ? 'Uploading…' : 'Upload photos'}
                  </Button>
                );
              })()}
            </div>
            {renderPhotos()}
          </div>

          <hr
            style={{
              border: 'none',
              borderTop: '1px solid var(--border)',
              margin: 0,
            }}
          />

          {isEditing ? (
            <>
              {/* User Information */}
              <div>
                <div
                  style={{
                    fontWeight: 700,
                    fontSize: 'var(--fs-md)',
                    color: 'var(--text)',
                    marginBottom: 16,
                  }}
                >
                  User Information
                </div>
                {renderUserInfoEdit()}
              </div>

              <hr
                style={{
                  border: 'none',
                  borderTop: '1px solid var(--border)',
                  margin: 0,
                }}
              />

              {/* Profile Fields */}
              <div>
                <div
                  style={{
                    fontWeight: 700,
                    fontSize: 'var(--fs-md)',
                    color: 'var(--text)',
                    marginBottom: 16,
                  }}
                >
                  Profile Fields
                </div>
                <div style={{ marginBottom: 16 }}>
                  <Input
                    placeholder="Search profile fields..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    style={{ maxWidth: 400 }}
                  />
                </div>
                {renderProfileFields()}
              </div>
            </>
          ) : (
            <UserProfileReadOnly
              user={selectedUser}
              excludeKeys={['summary']}
            />
          )}

          {/* Bio — the single home for the user's summary. Read-only by
              default; editable via the global Edit Profile flow. A generated
              draft (AI) appears beneath the current summary for review/save.
              `summary` is excluded from the grouped fields above to avoid
              duplication; the shared read-only view still shows Bio elsewhere
              (e.g. the attendee modal). */}
          <hr
            style={{
              border: 'none',
              borderTop: '1px solid var(--border)',
              margin: 0,
            }}
          />
          <div>
            <div
              className="row"
              style={{
                alignItems: 'center',
                justifyContent: 'space-between',
                marginBottom: 16,
              }}
            >
              <span
                style={{
                  fontWeight: 700,
                  fontSize: 'var(--fs-md)',
                  color: 'var(--text)',
                }}
              >
                Bio
              </span>
              <Button
                size="sm"
                variant="secondary"
                onClick={onGenSummary}
                disabled={genSummaryStatus.isLoading}
              >
                {genSummaryStatus.isLoading ? 'Generating…' : 'Generate'}
              </Button>
            </div>

            {/* Current summary: read-only by default, editable in Edit mode
                (persisted via the global Save Profile button). */}
            <Card pad>
              {isEditing ? (
                <Textarea
                  value={user.profile?.summary || ''}
                  onChange={(e) =>
                    onFieldValueChange('summary', e.target.value)
                  }
                  placeholder="No bio yet."
                  style={{ minHeight: 160, whiteSpace: 'pre-wrap' }}
                />
              ) : (
                <span
                  style={{
                    whiteSpace: 'pre-wrap',
                    color: user.profile?.summary
                      ? 'var(--text)'
                      : 'var(--text-faint)',
                    fontSize: 'var(--fs-sm)',
                  }}
                >
                  {user.profile?.summary || 'No bio yet.'}
                </span>
              )}
              {genSummaryStatus.isLoading && (
                <div
                  className="row"
                  style={{
                    alignItems: 'center',
                    gap: 8,
                    color: 'var(--text-muted)',
                    marginTop: 12,
                  }}
                >
                  <Spinner size="sm" />
                  <span style={{ fontSize: 'var(--fs-sm)' }}>
                    Generating a new summary…
                  </span>
                </div>
              )}
            </Card>

            {/* Generated draft: appears beneath the current summary after
                generating; saving replaces the bio and returns to default. */}
            {genDraft !== null && (
              <div style={{ marginTop: 16 }}>
                <div
                  style={{
                    fontWeight: 600,
                    fontSize: 'var(--fs-sm)',
                    color: 'var(--text-secondary)',
                    marginBottom: 8,
                  }}
                >
                  Generated summary
                </div>
                <Card pad>
                  <Textarea
                    value={genDraft ?? ''}
                    onChange={(e) => setGenDraft(e.target.value)}
                    style={{ minHeight: 160, whiteSpace: 'pre-wrap' }}
                  />
                  <div
                    className="row"
                    style={{
                      justifyContent: 'flex-end',
                      gap: 8,
                      marginTop: 12,
                    }}
                  >
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => setGenDraft(null)}
                      disabled={isSavingSummary}
                    >
                      Discard
                    </Button>
                    <Button
                      variant="primary"
                      size="sm"
                      onClick={onSaveSummary}
                      disabled={isSavingSummary || !genDraft.trim()}
                    >
                      {isSavingSummary ? 'Saving…' : 'Save Summary'}
                    </Button>
                  </div>
                </Card>
              </div>
            )}
          </div>

          <hr
            style={{
              border: 'none',
              borderTop: '1px solid var(--border)',
              margin: 0,
            }}
          />
          <HiddenProfileFields
            userId={user.id}
            hiddenFields={user.profile?.hiddenProfileFields}
            onSaved={(hiddenProfileFields) =>
              setUser((prev) =>
                prev
                  ? {
                      ...prev,
                      profile: { ...prev.profile, hiddenProfileFields },
                    }
                  : prev
              )
            }
          />
        </div>
      </div>

      {/* Profile Download Modal */}
      <ProfileModal
        isModalOpen={isProfileModalOpen}
        onModalClose={onProfileModalClose}
        user={user}
        optionsMap={optionsMap}
      />

      {/* Delete Confirmation Modal */}
      <Modal
        open={isDeleteOpen}
        onClose={() => setIsDeleteOpen(false)}
        title="Delete User"
        footer={
          <div className="row" style={{ gap: 8, justifyContent: 'flex-end' }}>
            <Button variant="ghost" onClick={() => setIsDeleteOpen(false)}>
              Cancel
            </Button>
            <Button variant="primary" onClick={onDelete}>
              Delete User
            </Button>
          </div>
        }
      >
        <p style={{ fontSize: 'var(--fs-sm)', color: 'var(--text)' }}>
          Are you sure you want to delete user <strong>{user?.email}</strong>?
          <br />
          <br />
          This action cannot be undone and will permanently remove:
          <br />
          &bull; User profile and all personal data
          <br />
          &bull; All photos and media
          <br />
          &bull; Conversation history
          <br />
          &bull; Match history and suggestions
        </p>
      </Modal>

      {/* Delete Photo Confirmation Modal */}
      <Modal
        open={isDeletePhotoOpen}
        onClose={() => {
          setIsDeletePhotoOpen(false);
          setPhotoPendingDelete(null);
        }}
        title="Delete photo"
        footer={
          <div className="row" style={{ gap: 8, justifyContent: 'flex-end' }}>
            <Button
              variant="ghost"
              onClick={() => {
                setIsDeletePhotoOpen(false);
                setPhotoPendingDelete(null);
              }}
            >
              Cancel
            </Button>
            <Button
              variant="primary"
              onClick={onConfirmDeletePhoto}
              disabled={isDeletingPhoto}
            >
              {isDeletingPhoto ? 'Deleting…' : 'Delete'}
            </Button>
          </div>
        }
      >
        <p style={{ fontSize: 'var(--fs-sm)', color: 'var(--text)' }}>
          Permanently delete this photo from <strong>{user?.email}</strong>?
          This cannot be undone.
        </p>
      </Modal>
    </div>
  );
};

export default UserProfileEditor;
